import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/auth-context';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { 
  Users, 
  UserPlus,
  Shield, 
  Trash2, 
  Edit,
  Save,
  Loader2,
  AlertTriangle,
  Building,
  Globe,
  MapPin,
  Link as LinkIcon,
  Phone,
  Mail,
  Briefcase,
  Calendar,
  CheckCircle,
  XCircle
} from 'lucide-react';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { JoinRequestsList } from './join-requests-list';

interface CompanyAdminViewProps {
  companyId: string;
}

export function CompanyAdminView({ companyId }: CompanyAdminViewProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [company, setCompany] = useState<any>(null);
  const [accountManager, setAccountManager] = useState<any>(null);
  const [client, setClient] = useState<any>(null);
  const [companyUsers, setCompanyUsers] = useState<any[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editedCompany, setEditedCompany] = useState<any>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [addUserDialogOpen, setAddUserDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [selectedRole, setSelectedRole] = useState('member');
  const [isAddingUser, setIsAddingUser] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');

  useEffect(() => {
    if (user && companyId) {
      fetchCompanyData();
    }
  }, [user, companyId]);

  const fetchCompanyData = async () => {
    try {
      // Fetch company with client data
      const { data: companyData, error: companyError } = await supabase
        .from('companies')
        .select(`
          *,
          client:clients(
            id,
            status,
            client_tier,
            annual_contract_value,
            customer_ltv,
            contract_renewal_date,
            satisfaction_score,
            account_manager_id,
            last_interaction_at,
            next_followup_at
          )
        `)
        .eq('id', companyId)
        .single();

      if (companyError) throw companyError;
      setCompany(companyData);
      setEditedCompany(companyData);
      setClient(companyData.client);

      // Fetch company users
      const { data: usersData, error: usersError } = await supabase
        .from('company_users')
        .select(`
          *,
          profile:profiles(
            id,
            full_name,
            email,
            avatar_url,
            title
          )
        `)
        .eq('company_id', companyId)
        .order('created_at', { ascending: false });

      if (usersError) throw usersError;
      setCompanyUsers(usersData || []);

      // If there's an account manager, fetch their profile
      if (companyData.client?.account_manager_id) {
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('id, full_name, avatar_url, email')
          .eq('id', companyData.client.account_manager_id)
          .single();

        if (profileError) throw profileError;
        setAccountManager(profileData);
      }
    } catch (error: any) {
      console.error('Error fetching company data:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to load company data'
      });
      navigate('/admin/companies');
    } finally {
      setLoading(false);
    }
  };

  const handleEditToggle = () => {
    if (isEditing) {
      // Discard changes if canceling edit mode
      setEditedCompany(company);
    }
    setIsEditing(!isEditing);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setEditedCompany(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSelectChange = (name: string, value: string) => {
    setEditedCompany(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const saveCompanyProfile = async () => {
    setIsSaving(true);
    try {
      // Only update fields that exist in the database schema
      const { error } = await supabase
        .from('companies')
        .update({
          name: editedCompany.name,
          industry: editedCompany.industry,
          // Remove fields that don't exist in the schema
          description: editedCompany.description,
          updated_at: new Date().toISOString()
        })
        .eq('id', companyId);

      if (error) throw error;

      // Update local state with edited values
      setCompany(editedCompany);
      setIsEditing(false);
      
      toast({
        title: 'Success',
        description: 'Company profile updated successfully'
      });
    } catch (error: any) {
      console.error('Error updating company profile:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.message || 'Failed to update company profile'
      });
    } finally {
      setIsSaving(false);
    }
  };

  const searchUsers = async (query: string) => {
    if (!query.trim() || query.length < 2) {
      setSearchResults([]);
      return;
    }

    try {
      let queryBuilder = supabase
        .from('profiles')
        .select('id, full_name, email, title, avatar_url')
        .or(`full_name.ilike.%${query}%,email.ilike.%${query}%`);

      // Only add not-in filter if there are existing users
      if (companyUsers.length > 0) {
        queryBuilder = queryBuilder.not('id', 'in', companyUsers.map(u => u.profile.id));
      }

      const { data, error } = await queryBuilder.limit(5);

      if (error) throw error;
      setSearchResults(data || []);
    } catch (error) {
      console.error('Error searching users:', error);
    }
  };

  const addUserToCompany = async () => {
    if (!selectedUser || !selectedRole) return;
    
    setIsAddingUser(true);
    try {
      const { error } = await supabase
        .from('company_users')
        .insert([{
          company_id: companyId,
          user_id: selectedUser.id,
          role: selectedRole,
          is_active: true,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }]);

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'User added to company successfully'
      });

      // Refresh company users
      fetchCompanyData();
      setAddUserDialogOpen(false);
      setSelectedUser(null);
      setSelectedRole('member');
    } catch (error: any) {
      console.error('Error adding user:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.message || 'Failed to add user'
      });
    } finally {
      setIsAddingUser(false);
    }
  };

  const updateUserRole = async (userId: string, newRole: string) => {
    try {
      const { error } = await supabase
        .from('company_users')
        .update({ 
          role: newRole,
          updated_at: new Date().toISOString()
        })
        .eq('company_id', companyId)
        .eq('user_id', userId);

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'User role updated successfully'
      });

      // Refresh company users
      fetchCompanyData();
    } catch (error: any) {
      console.error('Error updating user role:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.message || 'Failed to update user role'
      });
    }
  };

  const removeUser = async (userId: string) => {
    try {
      const { error } = await supabase
        .from('company_users')
        .delete()
        .eq('company_id', companyId)
        .eq('user_id', userId);

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'User removed from company successfully'
      });

      // Refresh company users
      fetchCompanyData();
    } catch (error: any) {
      console.error('Error removing user:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.message || 'Failed to remove user'
      });
    }
  };

  const handleDelete = async () => {
    if (deleteConfirmation !== 'Permanently Delete') {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Please type "Permanently Delete" to confirm'
      });
      return;
    }

    setIsDeleting(true);
    try {
      const { error } = await supabase
        .from('companies')
        .delete()
        .eq('id', companyId);

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Company deleted successfully'
      });
      navigate('/admin/companies');
    } catch (error: any) {
      console.error('Error deleting company:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.message || 'Failed to delete company'
      });
    } finally {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center p-6">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!company) {
    return (
      <div className="text-center py-8">
        <h2 className="text-2xl font-bold mb-2">Company Not Found</h2>
        <p className="text-muted-foreground mb-4">
          The requested company could not be found.
        </p>
        <Button onClick={() => navigate('/admin/companies')}>
          Return to Companies
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Company Logo and Actions */}
      <div className="flex justify-between items-start">
        <div className="flex items-center gap-4">
          <Avatar className="h-20 w-20 object-contain bg-white">
            <AvatarImage
              src={company?.logo_url}
              className="object-contain p-1"
              style={{ aspectRatio: 'auto' }}
            />
            <AvatarFallback>
              {company?.name?.[0]}
            </AvatarFallback>
          </Avatar>
          <div>
            <h1 className="text-2xl font-bold">{company?.name}</h1>
            <p className="text-muted-foreground">{company?.industry}</p>
          </div>
        </div>
        <div className="flex gap-2">
          {isEditing ? (
            <>
              <Button 
                variant="outline" 
                onClick={handleEditToggle}
              >
                Cancel
              </Button>
              <Button
                onClick={saveCompanyProfile}
                disabled={isSaving}
              >
                {isSaving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
            </>
          ) : (
            <>
              <Button 
                variant="outline"
                onClick={handleEditToggle}
              >
                <Edit className="h-4 w-4 mr-2" />
                Edit Profile
              </Button>
              <Button
                variant="destructive"
                onClick={() => setDeleteDialogOpen(true)}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Tabbed Interface for Company Information */}
      <Tabs 
        defaultValue="profile" 
        className="w-full"
        value={activeTab}
        onValueChange={setActiveTab}
      >
        <TabsList className="grid grid-cols-4 mb-6">
          <TabsTrigger value="profile">
            <Building className="h-4 w-4 mr-2" />
            Company Profile
          </TabsTrigger>
          <TabsTrigger value="culture">
            <Briefcase className="h-4 w-4 mr-2" />
            Culture & Benefits
          </TabsTrigger>
          <TabsTrigger value="team">
            <Users className="h-4 w-4 mr-2" />
            Team Members
          </TabsTrigger>
          <TabsTrigger value="requests">
            <Calendar className="h-4 w-4 mr-2" />
            Join Requests
          </TabsTrigger>
        </TabsList>

        {/* Company Profile Tab */}
        <TabsContent value="profile" className="space-y-6">
          <Card>
            <CardContent className="p-6">
              <div className="grid grid-cols-2 gap-6">
                {/* Basic Information */}
                <div className="space-y-4">
                  <h3 className="text-lg font-medium mb-4">Basic Information</h3>
                  
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Company Name</label>
                    {isEditing ? (
                      <Input
                        name="name"
                        value={editedCompany.name || ''}
                        onChange={handleInputChange}
                        placeholder="Company Name"
                      />
                    ) : (
                      <p>{company.name}</p>
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Industry</label>
                    {isEditing ? (
                      <Select 
                        value={editedCompany.industry || ''}
                        onValueChange={(value) => handleSelectChange('industry', value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select Industry" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="technology">Technology</SelectItem>
                          <SelectItem value="healthcare">Healthcare</SelectItem>
                          <SelectItem value="finance">Finance</SelectItem>
                          <SelectItem value="education">Education</SelectItem>
                          <SelectItem value="retail">Retail</SelectItem>
                          <SelectItem value="manufacturing">Manufacturing</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <p>{company.industry}</p>
                    )}
                  </div>
                </div>
                
                {/* Company Logo */}
                <div className="space-y-4">
                  <h3 className="text-lg font-medium mb-4">Company Logo</h3>
                  <div className="flex justify-center items-center h-full">
                    <Avatar className="h-32 w-32 object-contain bg-white">
                      <AvatarImage
                        src={company?.logo_url}
                        className="object-contain p-1"
                        style={{ aspectRatio: 'auto' }}
                      />
                      <AvatarFallback>
                        {company?.name?.[0]}
                      </AvatarFallback>
                    </Avatar>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-6">
              <h3 className="text-lg font-medium mb-4">Company Description</h3>
              
              {isEditing ? (
                <Textarea
                  name="description"
                  value={editedCompany.description || ''}
                  onChange={handleInputChange}
                  placeholder="Write a compelling description of your company..."
                  rows={6}
                  className="resize-none"
                />
              ) : (
                <div className="prose max-w-none">
                  <p>{company.description || "No description available."}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Culture & Benefits Tab */}
        <TabsContent value="culture" className="space-y-6">
          <Card>
            <CardContent className="p-6">
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium mb-4">Company Public Information</h3>
                  <p className="text-muted-foreground mb-6">
                    This information will be managed in the CompanyPublicInfo component.
                  </p>
                  
                  {/* Replace with your actual CompanyPublicInfo component */}
                  <div className="p-4 border rounded-md bg-muted/30">
                    <p className="text-center">
                      <Button
                        variant="outline"
                        onClick={() => setActiveTab('profile')}
                      >
                        <Edit className="h-4 w-4 mr-2" />
                        Edit Company Public Information
                      </Button>
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Team Members Tab */}
        <TabsContent value="team" className="space-y-6">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-lg font-medium">Company Team Members</h3>
                  <p className="text-sm text-muted-foreground">
                    Manage user access and roles
                  </p>
                </div>
                <Button onClick={() => setAddUserDialogOpen(true)}>
                  <UserPlus className="h-4 w-4 mr-2" />
                  Add Team Member
                </Button>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                {companyUsers.map((user) => (
                  <div 
                    key={user.id} 
                    className="flex items-center justify-between p-4 rounded-lg border bg-card hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <Avatar>
                        <AvatarImage src={user.profile.avatar_url} />
                        <AvatarFallback>
                          {user.profile.full_name?.[0]}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-medium">{user.profile.full_name}</h3>
                          {user.role === 'admin' && (
                            <Badge variant="secondary">
                              <Shield className="h-3 w-3 mr-1" />
                              Admin
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">{user.profile.email}</p>
                        {user.profile.title && (
                          <p className="text-sm text-muted-foreground">{user.profile.title}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Select
                        value={user.role}
                        onValueChange={(value) => updateUserRole(user.profile.id, value)}
                      >
                        <SelectTrigger className="w-[140px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="admin">Admin</SelectItem>
                          <SelectItem value="member">Member</SelectItem>
                          <SelectItem value="recruiter">Recruiter</SelectItem>
                          <SelectItem value="hiring-manager">Hiring Manager</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button 
                        variant="destructive" 
                        size="icon"
                        onClick={() => removeUser(user.profile.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>

              {companyUsers.length === 0 && (
                <div className="text-center py-6">
                  <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-medium mb-2">No Team Members</h3>
                  <p className="text-muted-foreground">
                    Add team members to give them access to this company.
                  </p>
                  <Button 
                    onClick={() => setAddUserDialogOpen(true)}
                    className="mt-4"
                  >
                    <UserPlus className="h-4 w-4 mr-2" />
                    Add First Team Member
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Join Requests Tab */}
        <TabsContent value="requests">
          <JoinRequestsList companyId={companyId} />
        </TabsContent>
      </Tabs>

      {/* Add User Dialog */}
      <Dialog open={addUserDialogOpen} onOpenChange={setAddUserDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Team Member</DialogTitle>
            <DialogDescription>
              Search and add users to the company
            </DialogDescription>
          </DialogHeader>

          <Command className="rounded-lg border shadow-md">
            <CommandInput 
              placeholder="Search by name or email..."
              value={searchQuery}
              onValueChange={(value) => {
                setSearchQuery(value);
                searchUsers(value);
              }}
            />
            <CommandList>
              <CommandEmpty>
                {searchQuery.length < 2 ? (
                  "Type at least 2 characters to search..."
                ) : (
                  "No users found"
                )}
              </CommandEmpty>
              <CommandGroup>
                {searchResults.map((user) => (
                  <CommandItem
                    key={user.id}
                    onSelect={() => setSelectedUser(user)}
                    className="cursor-pointer"
                  >
                    <div className="flex items-center gap-2">
                      <Avatar className="h-8 w-8">
                        <AvatarImage src={user.avatar_url} />
                        <AvatarFallback>
                          {user.full_name?.[0] || user.email?.[0]}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium">{user.full_name}</p>
                        <p className="text-sm text-muted-foreground">{user.email}</p>
                      </div>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>

          {selectedUser && (
            <div className="space-y-4 mt-4">
              <div className="flex items-center gap-4 p-4 rounded-lg border">
                <Avatar>
                  <AvatarImage src={selectedUser.avatar_url} />
                  <AvatarFallback>
                    {selectedUser.full_name?.[0] || selectedUser.email?.[0]}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <h3 className="font-medium">{selectedUser.full_name}</h3>
                  <p className="text-sm text-muted-foreground">{selectedUser.email}</p>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Role</label>
                <Select value={selectedRole} onValueChange={setSelectedRole}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="member">Member</SelectItem>
                    <SelectItem value="recruiter">Recruiter</SelectItem>
                    <SelectItem value="hiring-manager">Hiring Manager</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setAddUserDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={addUserToCompany}
              disabled={!selectedUser || !selectedRole || isAddingUser}
            >
              {isAddingUser ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Adding...
                </>
              ) : (
                'Add User'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Company</AlertDialogTitle>
            <AlertDialogDescription className="space-y-4">
              <div className="flex items-center gap-2 text-destructive">
                <AlertTriangle className="h-5 w-5" />
                <span className="font-medium">This action cannot be undone</span>
              </div>
              <p>
                This will permanently delete {company.name} and all associated data including:
              </p>
              <ul className="list-disc pl-4 space-y-1">
                <li>Company profile and settings</li>
                <li>All team members and roles</li>
                <li>Client information and metrics</li>
                <li>Join requests and history</li>
              </ul>
              <div>
                <p className="font-medium mb-2">Type "Permanently Delete" to confirm:</p>
                <Input
                  value={deleteConfirmation}
                  onChange={(e) => setDeleteConfirmation(e.target.value)}
                  placeholder="Permanently Delete"
                />
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground"
              disabled={deleteConfirmation !== 'Permanently Delete' || isDeleting}
            >
              {isDeleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete Company'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}